import {
  ApolloClient,
  ApolloProvider,
  FetchResult,
  from,
  HttpLink,
  InMemoryCache,
  NextLink,
  NormalizedCacheObject,
  Observable,
  Operation,
  ServerError,
  ServerParseError,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { useMonitoringClient } from '@xometry/ui';
import { ENV } from 'common/constants/env';
import {
  clearStorage,
  createRedirectUrl,
  getAuthToken,
  getRefreshToken,
  redirectTo404,
  redirectTo503,
  setAuthToken,
  setRefreshToken,
} from 'common/requests';
import { useAntNotificationWithContext } from 'components/ant/AntNotification';
import { isExpectedGQLError } from 'core/app/GQLProvider/GQLProvider.utils';
import stringify from 'fast-safe-stringify';
import { GraphQLError, Kind } from 'graphql/index';
import {
  ContactsRefreshTokenDocument,
  ContactsRefreshTokenMutation,
} from 'graphql/mutations/__generated__/contactsRefreshToken';
import { t } from 'i18next';
import { FC, ReactNode, useEffect, useMemo, useRef } from 'react';

interface Props {
  children: ReactNode;
}

const promiseToObservable = (promise: Promise<unknown>) =>
  new Observable((subscriber) => {
    promise.then(
      (value) => {
        if (!subscriber.closed) {
          subscriber.next(value);
          subscriber.complete();
        }
      },
      (err) => subscriber.error(err),
    );
  });

export const GQLProvider: FC<Props> = (props) => {
  const { children } = props;
  const monitoringClient = useMonitoringClient();
  const clientRef = useRef<ApolloClient<NormalizedCacheObject> | null>(null);
  const [{ error }, notificationContextHolder] = useAntNotificationWithContext();
  const client = useMemo(() => {
    const cache = new InMemoryCache({
      addTypename: true,
      possibleTypes: { UnifiedOffer: ['Offer', 'JobOffer'] },
    });
    const authLink = setContext((_, { headers }) => {
      const token = getAuthToken();

      return {
        // Why it is typed as any, apollo?
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        headers: {
          ...headers,
          accept: 'application/json',
          authorization: token ? `Bearer ${token}` : '',
        },
        credentials: 'include',
      };
    });
    const httpLink = new HttpLink({
      uri: (operation) => `${ENV.API_ENDPOINT}/partners/graphql?${operation.operationName}`,
    });

    const networkErrorHandler = (
      operation: Operation,
      networkError: ServerError | ServerParseError,
      forward: NextLink,
    ) => {
      const statusCode = networkError.statusCode;
      const token = getAuthToken();
      const currentRefreshToken = getRefreshToken();

      if (statusCode === 401 || statusCode === 403) {
        clearStorage();

        if (!token || !currentRefreshToken || !clientRef.current) {
          return;
        }

        return promiseToObservable(
          clientRef.current
            .mutate({
              mutation: ContactsRefreshTokenDocument,
              variables: { refreshToken: currentRefreshToken },
            })
            .then((r: FetchResult<ContactsRefreshTokenMutation>) => {
              const data = r.data?.contactsRefreshToken;

              if (data) {
                const { accessToken, refreshToken } = data || {};

                if (accessToken) {
                  setAuthToken(accessToken);
                }

                if (refreshToken) {
                  setRefreshToken(refreshToken);
                }

                operation.setContext({
                  headers: {
                    authorization: token ? `Bearer ${accessToken || ''}` : '',
                    accept: 'application/json',
                  },
                });
              } else {
                window.location.assign(createRedirectUrl());
              }
            })
            .catch((e: unknown) => {
              console.error(e);

              window.location.assign(createRedirectUrl());
            }),
        ).flatMap(() => forward(operation));
      }

      if (statusCode === 404) {
        redirectTo404();

        return;
      }

      if (statusCode === 503) {
        redirectTo503();

        return;
      }

      // The raw error stays in the monitoring report below; the user gets a translated generic message.
      // `t` comes from the i18next instance rather than `useTranslation`, so a language change does not
      // rebuild the client (and drop its cache) through the `useMemo` dependencies.
      error({ message: t('commonPhrases.networkError') });

      monitoringClient.captureError(networkError, {
        level: 'warning',
        extras: {
          networkError: stringify(networkError),
        },
      });
    };

    const graphQLErrorHandler = (rawErrors: readonly GraphQLError[], operation: Operation) => {
      rawErrors.forEach((rawError) => {
        error({
          title: t('commonPhrases.error'),
          description: rawError.message,
          style: { zIndex: 10001 },
          duration: 3,
        });

        try {
          if (isExpectedGQLError({ gqlError: rawError, operationName: operation.operationName })) {
            return;
          }

          const message =
            rawError?.message && Array.isArray(rawError?.path)
              ? `[${rawError.path.join('/')}]: ${rawError.message}`
              : rawError?.originalError?.message;

          monitoringClient.captureError(message ? new Error(message) : rawError, {
            level: 'warning',
            extras: {
              rawError: stringify(rawError),
            },
          });
        } catch (e: unknown) {
          monitoringClient.captureError(e instanceof Error ? e : new Error(String(e)), {
            level: 'warning',
            extras: {
              customError: e,
              rawError: stringify(rawError),
            },
          });
        }
      });
    };

    const retryLink = new RetryLink({
      delay: {
        initial: 2000,
        jitter: true,
        max: 60000,
      },
      attempts: {
        max: 6,
        retryIf: (error, operation) => {
          const operationDefinition = operation.query.definitions.find((d) => d.kind === Kind.OPERATION_DEFINITION);

          if (operationDefinition?.operation === 'mutation') return false;

          const statusCode = (error as { statusCode?: number })?.statusCode;

          if (typeof statusCode === 'number' && !isNaN(statusCode)) {
            return statusCode >= 500;
          }

          return Boolean(error);
        },
      },
    });

    const errorLink = onError((errorData) => {
      const { graphQLErrors, networkError, operation, forward } = errorData;

      if (graphQLErrors) {
        graphQLErrorHandler(graphQLErrors as unknown as GraphQLError[], operation);
      }

      if (networkError && 'statusCode' in networkError) {
        return networkErrorHandler(operation, networkError, forward);
      }

      // No response at all (offline, a dropped connection): `fetch` rejects with a `TypeError`. A query only
      // gets here once the retries are exhausted. Nothing to report — monitoring drops it too.
      if (networkError instanceof TypeError) {
        error({ title: t('commonPhrases.networkError') });
      }
    });

    const link = from([errorLink, retryLink, authLink.concat(httpLink)]);

    return new ApolloClient({ cache, link });
  }, [error, monitoringClient]);

  useEffect(() => {
    clientRef.current = client;
  }, [client]);

  return (
    <ApolloProvider client={client}>
      {notificationContextHolder}
      {children}
    </ApolloProvider>
  );
};
